import { Stack } from '@mui/material'
import type { CustomerUser } from '@einwert/valuation-api-client'
import {
    isValidDayjsDateOrNull,
    LoadingSkeleton,
    OrderStatus,
    OrderStatusFilter,
    OrderTitleFilter,
    OrderUserFilter,
    tokens,
} from 'ComponentLibrary'
import { Dayjs } from 'dayjs'
import { OrderPortfolioFilter } from 'ComponentLibrary/src/molecules/OrderFilters/OrderPortfolioFilter/OrderPortfolioFilter'
import { DateRangeSchema, PortfolioMetadataSchema } from 'ClientPortal/src/pages/schemas.zod'
import { z } from 'zod'
import { OrderDateFilter } from 'ComponentLibrary/src/molecules/OrderFilters'
import { ArrowCounterClockwiseIcon, CheckIcon, SortAscendingIcon } from '@phosphor-icons/react'
import { IconButton, IconButtonWithOptions } from 'ComponentLibrary/src/piazza'
import { OrderSortOption } from '../OrderListPageContent'
import { useTranslation } from 'react-i18next'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

const ORDER_STATUS_KEYS: OrderStatus[] = [
    'open',
    'accepted',
    'in_communication_with_owner',
    'inspection_scheduled',
    'in_progress',
    'in_review',
    'done',
    'blocked',
    'on_hold',
    'temp_hide_in_portal',
    'canceled',
]

type PortfolioMetadata = z.infer<typeof PortfolioMetadataSchema>
type DateRange = z.infer<typeof DateRangeSchema>
export type OrderFilterProps = {
    defaultOrderTitleFilter?: string
    isPending?: boolean
    customerUsers: CustomerUser[]
    setStatuses: (statuses: OrderStatus[]) => void
    statuses: OrderStatus[]
    setAssignedCustomers: (assignedCustomers: number[]) => void
    assignedCustomers: number[]
    setTitleFilter: (titleFilter: string) => void
    setStartValuationDate: (start: Dayjs | null) => void
    setEndValuationDate: (end: Dayjs | null) => void
    valuationDateRange: DateRange
    setPortfolioFilter: (portfolio: PortfolioMetadata | undefined) => void
    portfolioOptions: PortfolioMetadata[]
    portfolioFilter?: PortfolioMetadata
    resetAllFilters: () => void
    areFiltersEmpty: boolean
    sortOption: OrderSortOption
    onSortChange: (option: OrderSortOption) => void
}

export function OrderListFilter({
    defaultOrderTitleFilter,
    isPending,
    customerUsers,
    setStatuses,
    statuses,
    setAssignedCustomers,
    assignedCustomers,
    setTitleFilter,
    setStartValuationDate,
    setEndValuationDate,
    valuationDateRange,
    portfolioOptions,
    portfolioFilter,
    setPortfolioFilter,
    resetAllFilters,
    areFiltersEmpty,
    sortOption,
    onSortChange,
}: OrderFilterProps) {
    const { t } = useTranslation('pages', { keyPrefix: 'orders.orderListPage' })
    const { t: tTerminology } = useTranslation('terminology', { keyPrefix: 'common' })
    const locale = useCurrentResolvedLocale()
    const statusLabels = ORDER_STATUS_KEYS.reduce(
        (labels, status) => ({ ...labels, [status]: tTerminology(`orderStatuses.${status}.label`) }),
        {} as Partial<Record<OrderStatus, string>>
    )

    if (isPending) {
        return (
            <Stack direction="row" gap={tokens.SizingSpacing150} alignItems="center" minWidth="1280px">
                <LoadingSkeleton variant="rounded" count={6} width="100%" height="40px" />
                <Stack direction="row" gap={tokens.SizingSpacing150} alignItems="center">
                    <LoadingSkeleton variant="rounded" count={2} width="32px" height="32px" />
                </Stack>
            </Stack>
        )
    }

    return (
        <Stack direction="row" alignItems="center" spacing="10px" justifyItems="center" alignContent="center" minWidth="1280px">
            <OrderTitleFilter defaultOrderTitleFilter={defaultOrderTitleFilter} setFilter={setTitleFilter} label={t('filters.search')} />
            <OrderDateFilter
                label={t('filters.valuationDateFrom')}
                value={valuationDateRange.start}
                onChange={(newValue) => isValidDayjsDateOrNull(newValue) && setStartValuationDate(newValue)}
                locale={locale}
            />
            <OrderDateFilter
                label={t('filters.valuationDateTo')}
                value={valuationDateRange.end}
                onChange={(newValue) => isValidDayjsDateOrNull(newValue) && setEndValuationDate(newValue)}
                locale={locale}
            />
            {portfolioOptions.length > 0 && (
                <OrderPortfolioFilter
                    portfolios={portfolioOptions}
                    portfolio={portfolioFilter}
                    setPortfolio={(newPortfolio) => setPortfolioFilter(newPortfolio)}
                    inputLabel={tTerminology('portfolio')}
                />
            )}
            <OrderStatusFilter setStatuses={setStatuses} statuses={statuses} inputLabel={t('filters.status')} statusLabels={statusLabels} />
            <OrderUserFilter
                label={t('filters.assignee')}
                users={customerUsers}
                setAssignedUsers={setAssignedCustomers}
                filteredUsers={assignedCustomers}
                loadingText={t('filters.assigneeOptionsLoading')}
                emptyText={t('filters.assigneeOptionsEmpty')}
            />
            <IconButton
                size="small"
                variant="secondary"
                disabled={areFiltersEmpty}
                icon={<ArrowCounterClockwiseIcon />}
                onClick={resetAllFilters}
                tooltip={t('filters.resetAll')}
                ariaLabel={t('filters.resetAll')}
            />
            <IconButtonWithOptions
                size="small"
                variant="secondary"
                ariaLabel={t('filters.sortAriaLabel')}
                tooltip={t('filters.sortTooltip')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                icon={<SortAscendingIcon />}
                options={[
                    {
                        title: t('filters.sortByOrderDate'),
                        callback: () => onSortChange('orderDate'),
                        icon: sortOption === 'orderDate' ? <CheckIcon /> : <CheckIcon style={{ visibility: 'hidden' }} />,
                    },
                    {
                        title: t('filters.sortAlphabetically'),
                        callback: () => onSortChange('alphabetical'),
                        icon: sortOption === 'alphabetical' ? <CheckIcon /> : <CheckIcon style={{ visibility: 'hidden' }} />,
                    },
                ]}
            />
        </Stack>
    )
}
